const defaultLocale = 'en-AU';

// Supported languages based on odin-translations (filtered to available primelocale files)
const supportedLocales = [
  'ja', // Japanese
  'zh-CN', // Chinese (Simplified) - Note: primelocale uses zh-CN instead of zh
  'zh-TW', // Chinese (Traditional)
  'fi', // Finnish
  'tl', // Filipino
  'hr', // Croatian
  'es', // Spanish
  'fr', // French
  'de', // German
  'it', // Italian
  'pt-BR', // Portuguese (Brazil)
  'pt', // Portuguese (Portugal)
  'hi', // Hindi
  'ar', // Arabic
  'ru', // Russian
  'ko', // Korean
  'bn', // Bengali
  'en', // English
  'en-GB', // English (United Kingdom)
  defaultLocale, // Always include default locale
];

// Vite-compatible loaders
const localeLoaders: Record<string, () => Promise<Record<string, unknown>>> = {
  ja: () => import('primelocale/js/ja.js'),
  'zh-CN': () => import('primelocale/js/zh_CN.js'),
  'zh-TW': () => import('primelocale/js/zh_TW.js'),
  fi: () => import('primelocale/js/fi.js'),
  tl: () => import('primelocale/js/tl.js'),
  hr: () => import('primelocale/js/hr.js'),
  es: () => import('primelocale/js/es.js'),
  fr: () => import('primelocale/js/fr.js'),
  de: () => import('primelocale/js/de.js'),
  it: () => import('primelocale/js/it.js'),
  'pt-BR': () => import('primelocale/js/pt_BR.js'),
  pt: () => import('primelocale/js/pt.js'),
  hi: () => import('primelocale/js/hi.js'),
  ar: () => import('primelocale/js/ar.js'),
  ru: () => import('primelocale/js/ru.js'),
  ko: () => import('primelocale/js/ko.js'),
  bn: () => import('primelocale/js/bn.js'),
  en: () => import('primelocale/js/en.js'),
  'en-GB': () => import('primelocale/js/en_GB.js'),
  [defaultLocale]: () => import('primelocale/js/en_AU.js'),
};

export const resolveLocale = async () => {
  try {
    const browserLocale = navigator?.language || defaultLocale;

    // Check if the browser locale is in our supported list
    let locale = browserLocale;
    if (!supportedLocales.includes(browserLocale)) {
      // Try to match language part only (e.g., 'en-US' -> 'en')
      const languageCode = browserLocale.split('-')[0];
      if (supportedLocales.includes(languageCode)) {
        locale = languageCode;
      } else {
        // Special case for Chinese: map 'zh' to 'zh-CN'
        if (languageCode === 'zh') {
          locale = 'zh-CN';
        } else {
          locale = defaultLocale;
        }
      }
    }

    // Use explicit loader for better Vite compatibility
    const loader = localeLoaders[locale] || localeLoaders[defaultLocale];
    const localeModule = await loader();
    const localeData = localeModule[locale.replace('-', '_')];
    return { locale, options: localeData };
  } catch {
    // Fallback to default locale if import fails
    const localeModule = await localeLoaders[defaultLocale]();
    const localeData = localeModule[defaultLocale.replace('-', '_')];
    return { locale: defaultLocale, options: localeData };
  }
};
